import * as React from 'react';
import DiMarkdown from './DiMarkdown';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ShareIcon from '@mui/icons-material/Share';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import PauseIcon from '@mui/icons-material/Pause';
import Tooltip from '@mui/material/Tooltip';
import TextareaAutosize from '@mui/material/TextareaAutosize';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import ProjectSocialShare from './ProjectSocialShare';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InsightSocialShare from './InsightSocialShare';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(
  props,
  ref,
) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Project({project: initialProject, sharedMode = false} :any) {
  const [project, setProject] = React.useState(initialProject);
  const [expandedInsight, setExpandedInsight] = React.useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [showDeleteTaskModal, setShowDeleteTaskModal] = React.useState(false);
  const [showResetProjectModal, setShowResetProjectModal] = React.useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = React.useState(false);
  const [insightToDelete, setInsightToDelete] = React.useState<string | null>(null);
  const [isPendingTasksExpanded, setIsPendingTasksExpanded] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [copyNotification, setCopyNotification] = React.useState<string | null>(null);
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);
  const [editedTitle, setEditedTitle] = React.useState(project.title);
  const [editedDescription, setEditedDescription] = React.useState(project.description);
  const [isEditingTask, setIsEditingTask] = React.useState<string | null>(null);
  const [editedTask, setEditedTask] = React.useState<any>(null);
  const [showNewTaskModal, setShowNewTaskModal] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
  const [newTask, setNewTask] = React.useState({
    content: '',
    task_type: 'immediate',
    recurring_frequency: '',
    incident_trigger: '',
    incident_response_plan: ''
  });
  const [taskSuccessMessage, setTaskSuccessMessage] = React.useState<string | null>(null);
  const [taskToDelete, setTaskToDelete] = React.useState<string | null>(null);
  const itemsPerPage = 5;
  const totalPages = Math.ceil((project.insights?.length || 0) / itemsPerPage);
  const [showPauseProjectModal, setShowPauseProjectModal] = React.useState(false);
  const [unauthorizedNotificationOpen, setUnauthorizedNotificationOpen] = React.useState(false);

  // Update useEffect to sync project with prop changes
  React.useEffect(() => {
    setProject(initialProject);
  }, [initialProject]);

  // Handle 401 unauthorized responses
  const handleUnauthorizedError = (response: Response) => {
    if (response.status === 401) {
      setUnauthorizedNotificationOpen(true);
      return true;
    }
    return false;
  };

  // Handle closing the unauthorized notification
  const handleUnauthorizedNotificationClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setUnauthorizedNotificationOpen(false);
  };

  const formatDateTime = (dateString: string) => {
    if (dateString === 'Not Yet') return dateString;
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const toggleInsight = (id: string) => {
    setExpandedInsight(prevId => prevId === id ? null : id);
  };

  const openDeleteModal = (id: string) => {
    setInsightToDelete(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setInsightToDelete(null);
  };

  const deleteInsight = async () => {
    if (insightToDelete) {
      try {
        const response = await fetch(`/insights/${insightToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          },
        });

        if (response.ok) {
          setProject(prev => ({
            ...prev,
            insights: prev.insights.filter((insight) => insight.id !== insightToDelete)
          }));
          closeDeleteModal();
        } else if (handleUnauthorizedError(response)) {
          // Unauthorized error handled by the error handler
          return;
        } else {
          console.error('Failed to delete insight');
        }
      } catch (error) {
        console.error('Error deleting insight:', error);
      }
    }
  };

  const togglePendingTasks = () => {
    setIsPendingTasksExpanded(!isPendingTasksExpanded);
  };

  const getCurrentPageInsights = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    if (!project.insights) return [];
    return project.insights.slice(startIndex, startIndex + itemsPerPage);
  };

  const copyToClipboard = (content: string, insightId: string) => {
    navigator.clipboard.writeText(content);
    setCopyNotification(insightId);
    // Clear the notification after 2 seconds
    setTimeout(() => {
      setCopyNotification(null);
    }, 2000);
  };

  const handleProjectUpdate = async (field: 'title' | 'description') => {
    const value = field === 'title' ? editedTitle : editedDescription;
    const originalValue = field === 'title' ? project.title : project.description;

    try {
      const response = await fetch(`/projects/${project.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ [field]: value })
      });

      if (response.ok) {
        setProject(prev => ({
          ...prev,
          [field]: value
        }));
        if (field === 'title') {
          setIsEditingTitle(false);
        } else {
          setIsEditingDescription(false);
        }
        setSaveMessage('Saved!');
        setTimeout(() => {
          setSaveMessage(null);
        }, 2000);
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      } else {
        if (field === 'title') {
          setEditedTitle(originalValue);
          setIsEditingTitle(false);
        } else {
          setEditedDescription(originalValue);
          setIsEditingDescription(false);
        }
        alert(`Failed to update ${field}. Please try again.`);
      }
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      if (field === 'title') {
        setEditedTitle(originalValue);
        setIsEditingTitle(false);
      } else {
        setEditedDescription(originalValue);
        setIsEditingDescription(false);
      }
      alert(`Failed to update ${field}. Please try again.`);
    }
  };

  const handleProjectReset = async () => {
    setShowResetProjectModal(true);
  };

  const confirmProjectReset = async () => {
    try {
      const response = await fetch(`/projects/${project.id}/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        }
      });

      if (response.ok) {
        // Fetch the updated project state
        const updatedResponse = await fetch(`/projects/${project.id}/data`);
        if (updatedResponse.ok) {
          const updatedProject = await updatedResponse.json();
          setProject(updatedProject);
        } else if (handleUnauthorizedError(updatedResponse)) {
          // Unauthorized error handled by the error handler
          return;
        }
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error('Error resetting project:', error);
    }
    setShowResetProjectModal(false);
  };

  const handleProjectDelete = async () => {
    setShowDeleteProjectModal(true);
  };

  const confirmProjectDelete = async () => {
    try {
      const response = await fetch(`/projects/${project.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        }
      });

      if (response.ok) {
        window.location.href = '/my_projects';
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error('Error deleting project:', error);
    }
    setShowDeleteProjectModal(false);
  };

  const handleTaskUpdate = async (taskId: string) => {
    try {
      const response = await fetch(`/tasks/${taskId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify(editedTask)
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setProject(prev => ({
          ...prev,
          pending_tasks: prev.pending_tasks.map((task: any) =>
            task.id === taskId ? updatedTask : task
          )
        }));
        setIsEditingTask(null);
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleTaskDelete = async (taskId: string) => {
    setTaskToDelete(taskId);
    setShowDeleteTaskModal(true);
  };

  const confirmTaskDelete = async () => {
    if (taskToDelete) {
      try {
        const response = await fetch(`/tasks/${taskToDelete}`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          }
        });

        if (response.ok) {
          setProject(prev => ({
            ...prev,
            pending_tasks: prev.pending_tasks.filter((task: any) => task.id !== taskToDelete)
          }));
        } else if (handleUnauthorizedError(response)) {
          // Unauthorized error handled by the error handler
          return;
        }
      } catch (error) {
        console.error('Error deleting task:', error);
      }
    }
    setShowDeleteTaskModal(false);
    setTaskToDelete(null);
  };

  const handleNewTaskSubmit = async () => {
    try {
      const response = await fetch(`/projects/${project.id}/tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify(newTask)
      });

      if (response.ok) {
        // Fetch the updated project state
        const updatedResponse = await fetch(`/projects/${project.id}/data`);
        if (updatedResponse.ok) {
          const updatedProject = await updatedResponse.json();
          setProject(updatedProject);
        } else if (handleUnauthorizedError(updatedResponse)) {
          // Unauthorized error handled by the error handler
          return;
        }
        setShowNewTaskModal(false);
        setNewTask({
          content: '',
          task_type: 'immediate',
          recurring_frequency: '',
          incident_trigger: '',
          incident_response_plan: ''
        });
        // Show success message
        setTaskSuccessMessage('Task created successfully!');
        // Clear success message after 3 seconds
        setTimeout(() => {
          setTaskSuccessMessage(null);
        }, 3000);
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const refreshProject = async () => {
    try {
      const response = await fetch(`/projects/${project.id}/data`);
      if (response.ok) {
        const updatedProject = await response.json();
        setProject(prevData => ({
          ...prevData,
          ...updatedProject,
          insights: updatedProject.insights ?? []
        }));
        // Reset to first page when refreshing
        setCurrentPage(1);
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error('Error refreshing project:', error);
    }
  };

  const handleProjectPause = async () => {
    const newStatus = project.status === 'active' ? 'paused' : 'active';
    const actionText = newStatus === 'paused' ? 'pause' : 'resume';
    setShowPauseProjectModal(true);
  };

  const confirmProjectPause = async () => {
    const newStatus = project.status === 'active' ? 'paused' : 'active';
    try {
      const response = await fetch(`/projects/${project.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        setProject(prev => ({
          ...prev,
          status: newStatus
        }));
      } else if (handleUnauthorizedError(response)) {
        // Unauthorized error handled by the error handler
        return;
      }
    } catch (error) {
      console.error(`Error ${newStatus === 'paused' ? 'pausing' : 'resuming'} project:`, error);
    }
    setShowPauseProjectModal(false);
  };

  return (
    <div>
      <div className="page-header">
        <h3 className="page-title"> </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item"><a href="/my_projects">My Projects</a></li>
            <li className="breadcrumb-item active" aria-current="page">{project.title}</li>
          </ol>
        </nav>
      </div>
      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="project-header">
                {isEditingTitle && !sharedMode ? (
                  <input
                    type="text"
                    value={editedTitle}
                    onChange={(e) => setEditedTitle(e.target.value)}
                    onBlur={(e) => handleProjectUpdate('title')}
                    className="form-control project-title-input"
                    style={{ fontSize: '1.5rem', fontWeight: 600, padding: '0.5rem 1rem' }}
                  />
                ) : (
                  <h4
                    className="card-title project-title"
                    onClick={() => !sharedMode && setIsEditingTitle(true)}
                    style={{
                      fontSize: '2rem',
                      fontWeight: 700,
                      margin: 0,
                      padding: '0.5rem 1rem',
                      width: '100%',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: sharedMode ? 'default' : 'pointer'
                    }}
                    title={project.title}
                  >
                    {project.title}
                    {!sharedMode && <EditIcon fontSize="small" className="ml-2" />}
                  </h4>
                )}
              </div>

              <div className="row">
                <div className="project-description col-lg-9 col-md-12">
                  {isEditingDescription && !sharedMode ? (
                    <TextareaAutosize
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      onBlur={(e) => handleProjectUpdate('description')}
                      className="form-control"
                      minRows={4}
                      style={{ width: '100%', resize: 'vertical' }}
                    />
                  ) : (
                    <div className="card-description" onClick={() => !sharedMode && setIsEditingDescription(true)}>
                      <div style={{ flex: 1 }}>
                        <DiMarkdown>{project.description}</DiMarkdown>
                      </div>
                      {!sharedMode && <EditIcon fontSize="small" className="ml-2" style={{ flexShrink: 0 }} />}
                    </div>
                  )}

                  {project.autonomous && (
                    <div className="project-status">
                      <span className={`status-badge ${project.status}`}>
                        {project.status}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {!sharedMode && project.autonomous && (
                <div className="project-actions" style={{ width: '100%', marginTop: '1rem', marginBottom: '2rem' }}>
                  <Tooltip title="Refresh project">
                    <button onClick={refreshProject} className="btn btn-outline-primary">
                      <RefreshIcon fontSize="small" /> Refresh
                    </button>
                  </Tooltip>
                  <Tooltip title={project.status === 'active' ? "Pause project" : "Resume project"}>
                    <button onClick={handleProjectPause} className="btn btn-outline-warning">
                      <PauseIcon fontSize="small" /> {project.status === 'active' ? 'Pause' : 'Resume'}
                    </button>
                  </Tooltip>
                  <Tooltip title="Reset project">
                    <button onClick={handleProjectReset} className="btn btn-outline-success">
                      <RestartAltIcon fontSize="small" /> Reset
                    </button>
                  </Tooltip>
                  <ProjectSocialShare project={project} />
                  <Tooltip title="Delete project">
                    <button onClick={handleProjectDelete} className="btn btn-outline-danger">
                      <DeleteIcon fontSize="small" /> Delete
                    </button>
                  </Tooltip>
                </div>
              )}
              {/* Updated Pending Tasks Section */}
              {project.autonomous && (
                <div className="pending-tasks-section">
                  <div className="pending-tasks-header" onClick={togglePendingTasks}>
                    <h5 className="mb-3">
                      <AssignmentIcon className="mr-2" />
                      Pending Tasks
                      <span className="task-count">({project.pending_tasks?.length || 0})</span>
                      <span className="toggle-icon">{isPendingTasksExpanded ? '▼' : '▶'}</span>
                    </h5>
                    {!sharedMode && (
                      <Tooltip title="Add new pending task">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowNewTaskModal(true);
                          }}
                          className="btn btn-primary btn-sm add-task-btn"
                        >
                          <AddIcon fontSize="small" /> Add Task
                        </button>
                      </Tooltip>
                    )}
                  </div>

                  {isPendingTasksExpanded && (
                    <div className="row">
                      <div className="pending-tasks col-lg-9 col-md-12">
                        {project.pending_tasks && project.pending_tasks.length > 0 ? (
                          <ul className="task-list">
                            {project.pending_tasks.map((task: any) => (
                              <li key={task.id} className="task-item">
                                <div className="task-main">
                                  <div className="task-icon-wrapper">
                                    <span className="task-icon">
                                      {(task.task_type === 'immediate') || (task.task_type === 'conclusion') ? '⚡' : '🔄'}
                                    </span>
                                  </div>
                                  <div className="task-content">
                                    {isEditingTask === task.id && !sharedMode ? (
                                      <div className="task-edit-form">
                                        <TextareaAutosize
                                          value={editedTask.content}
                                          onChange={(e) => setEditedTask({...editedTask, content: e.target.value})}
                                          className="form-control mb-2"
                                          placeholder="Enter task content"
                                          minRows={3}
                                          style={{ width: '100%', resize: 'vertical' }}
                                        />
                                        <select
                                          value={editedTask.task_type}
                                          onChange={(e) => setEditedTask({...editedTask, task_type: e.target.value})}
                                          className="form-control mb-2"
                                        >
                                          <option value="immediate">Immediate</option>
                                          <option value="recurring">Recurring</option>
                                          <option value="conclusion">Conclusion</option>
                                        </select>
                                        {editedTask.task_type === 'recurring' && (
                                          <>
                                            <select
                                              value={editedTask.recurring_frequency}
                                              onChange={(e) => setEditedTask({...editedTask, recurring_frequency: e.target.value})}
                                              className="form-control mb-2"
                                            >
                                              <option value="">Select Frequency</option>
                                              <option value="daily">Daily</option>
                                              <option value="weekly">Weekly</option>
                                              <option value="monthly">Monthly</option>
                                            </select>
                                            <div className="form-group">
                                              <label htmlFor="edit_incident_trigger">Incident Trigger*</label>
                                              <TextareaAutosize
                                                id="edit_incident_trigger"
                                                value={editedTask.incident_trigger}
                                                onChange={(e) => setEditedTask({...editedTask, incident_trigger: e.target.value})}
                                                className="form-control mb-2"
                                                placeholder="Describe what triggers this incident"
                                                minRows={3}
                                                style={{ width: '100%', resize: 'vertical' }}
                                                required
                                              />
                                            </div>
                                            <div className="form-group">
                                              <label htmlFor="edit_incident_response_plan">Incident Response Plan*</label>
                                              <TextareaAutosize
                                                id="edit_incident_response_plan"
                                                value={editedTask.incident_response_plan}
                                                onChange={(e) => setEditedTask({...editedTask, incident_response_plan: e.target.value})}
                                                className="form-control mb-2"
                                                placeholder="Describe how to respond to this incident"
                                                minRows={3}
                                                style={{ width: '100%', resize: 'vertical' }}
                                                required
                                              />
                                            </div>
                                          </>
                                        )}
                                        <div className="task-edit-actions">
                                          <button onClick={() => handleTaskUpdate(task.id)} className="btn btn-success btn-sm">
                                            <SaveIcon fontSize="small" /> Save
                                          </button>
                                          <button onClick={() => setIsEditingTask(null)} className="btn btn-secondary btn-sm">
                                            <CloseIcon fontSize="small" /> Cancel
                                          </button>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div className="task-text">
                                          <DiMarkdown>{task.content}</DiMarkdown>
                                        </div>
                                        <div className="task-details">
                                          <div className="task-badges">
                                            <span className={`task-badge ${task.task_type}`}>
                                              {task.task_type === 'immediate' ? 'one-time' : task.task_type}
                                            </span>
                                            {task.recurring_frequency && task.task_type !== 'immediate' && (
                                              <span className="task-frequency">
                                                {task.recurring_frequency}
                                              </span>
                                            )}
                                          </div>
                                          {task.last_run_at && (
                                            <span className="task-last-run">
                                              Last run: {formatDateTime(task.last_run_at)}
                                            </span>
                                          )}
                                        </div>
                                        {task.task_type === 'recurring' && (task.incident_trigger || task.incident_response_plan) && (
                                          <div className="incident-info">
                                            {task.incident_trigger && (
                                              <div className="incident-section">
                                                <span className="incident-label">Trigger:</span>
                                                <span className="incident-text">{task.incident_trigger}</span>
                                              </div>
                                            )}
                                            {task.incident_response_plan && (
                                              <div className="incident-section">
                                                <span className="incident-label">Response Plan:</span>
                                                <span className="incident-text">{task.incident_response_plan}</span>
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {!sharedMode && (
                                          <div className="task-actions">
                                            <Tooltip title="Edit Task">
                                              <button onClick={() => {
                                                setIsEditingTask(task.id);
                                                setEditedTask({...task});
                                              }} className="btn btn-outline-primary btn-sm">
                                                <EditIcon fontSize="small" />
                                              </button>
                                            </Tooltip>
                                            <Tooltip title="Delete Task">
                                              <button onClick={() => handleTaskDelete(task.id)} className="btn btn-outline-danger btn-sm">
                                                <DeleteIcon fontSize="small" />
                                              </button>
                                            </Tooltip>
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="no-tasks">
                            <AssignmentOutlinedIcon sx={{ fontSize: 48, color: '#dee2e6', marginBottom: '1rem' }} />
                            <p>No pending tasks for this project.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Update the New Task Modal */}
              {showNewTaskModal && !sharedMode && (
                <div className="modal-overlay" onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowNewTaskModal(false);
                  }
                }}>
                  <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <h2>Create New Task</h2>
                    <form onSubmit={(e) => {
                      e.preventDefault();
                      handleNewTaskSubmit();
                    }}>
                      <div className="form-group">
                        <label htmlFor="content">Task Content*</label>
                        <TextareaAutosize
                          id="content"
                          value={newTask.content}
                          onChange={(e) => setNewTask({...newTask, content: e.target.value})}
                          className="form-control"
                          placeholder="Enter task content"
                          minRows={3}
                          style={{ width: '100%', resize: 'vertical' }}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="task_type">Task Type</label>
                        <select
                          id="task_type"
                          value={newTask.task_type}
                          onChange={(e) => setNewTask({...newTask, task_type: e.target.value})}
                          className="form-control"
                        >
                          <option value="immediate">Immediate</option>
                          <option value="recurring">Recurring</option>
                          <option value="conclusion">Conclusion</option>
                        </select>
                      </div>
                      {newTask.task_type === 'recurring' && (
                        <>
                          <div className="form-group">
                            <label htmlFor="recurring_frequency">Recurring Frequency*</label>
                            <select
                              id="recurring_frequency"
                              value={newTask.recurring_frequency}
                              onChange={(e) => setNewTask({...newTask, recurring_frequency: e.target.value})}
                              className="form-control"
                              required
                            >
                              <option value="">Select Frequency</option>
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </select>
                          </div>
                          <div className="form-group">
                            <label htmlFor="incident_trigger">Incident Trigger*</label>
                            <TextareaAutosize
                              id="incident_trigger"
                              value={newTask.incident_trigger}
                              onChange={(e) => setNewTask({...newTask, incident_trigger: e.target.value})}
                              className="form-control"
                              placeholder="Describe what triggers this incident"
                              minRows={3}
                              style={{ width: '100%', resize: 'vertical' }}
                              required
                            />
                          </div>
                          <div className="form-group">
                            <label htmlFor="incident_response_plan">Incident Response Plan*</label>
                            <TextareaAutosize
                              id="incident_response_plan"
                              value={newTask.incident_response_plan}
                              onChange={(e) => setNewTask({...newTask, incident_response_plan: e.target.value})}
                              className="form-control"
                              placeholder="Describe how to respond to this incident"
                              minRows={3}
                              style={{ width: '100%', resize: 'vertical' }}
                              required
                            />
                          </div>
                        </>
                      )}
                      <div className="modal-actions">
                        <button type="button" className="btn btn-secondary" onClick={() => setShowNewTaskModal(false)}>Cancel</button>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={newTask.task_type === 'recurring' && (!newTask.recurring_frequency || !newTask.incident_trigger || !newTask.incident_response_plan)}
                        >
                          Create Task
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {/* Add success message notification */}
              {taskSuccessMessage && (
                <div className="success-notification">
                  <span>{taskSuccessMessage}</span>
                </div>
              )}

              <h5 className="mt-4 mb-3">Insights & Reports</h5>
              <div className="row">
                <div className="col-lg-9 col-md-12">
                  {project.insights && project.insights.length > 0 ? (
                    <>
                      {getCurrentPageInsights().map((insight) => (
                        <div key={insight.id} className="insight-card" onClick={() => toggleInsight(insight.id)}>
                          <div className="insight-header">
                            <h6 className="insight-title">{insight.title}</h6>
                            <div className="insight-meta">
                              <span>AI: {insight.ai_assistant}</span>
                              <span>Created: {formatDateTime(insight.created_at)}</span>
                            </div>
                            <div className="insight-expand-indicator">
                              <Tooltip title={expandedInsight === insight.id ? "Collapse" : "Expand"}>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleInsight(insight.id);
                                  }}
                                  size="small"
                                  aria-label={expandedInsight === insight.id ? "Collapse" : "Expand"}
                                  className="expand-indicator-btn"
                                >
                                  {expandedInsight === insight.id ? <ExpandLessIcon fontSize="medium" /> : <ExpandMoreIcon fontSize="medium" />}
                                </IconButton>
                              </Tooltip>
                            </div>
                          </div>
                          {expandedInsight === insight.id && (
                            <div className="insight-content">
                              <p>
                                <DiMarkdown>{insight.content}</DiMarkdown>
                              </p>
                              {insight.task && (
                                <p><h3>Parent Task</h3>
                                <span>The above insights were generated for the following tasks:</span>
                                <DiMarkdown>{insight.task}</DiMarkdown>
                                </p>
                              )}
                            </div>
                          )}
                          <div className="insight-actions">
                            <Tooltip title="View Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`/insights/${insight.id}`, '_blank');
                                }}
                                size="small"
                                aria-label="View Insight"
                                className="insight-action-btn"
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Copy Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(insight.content, insight.id);
                                }}
                                size="small"
                                aria-label="Copy Insight"
                                className="insight-action-btn"
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Insight">
                              <IconButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const element = document.createElement("a");
                                  const file = new Blob([insight.content], { type: 'text/plain' });
                                  element.href = URL.createObjectURL(file);
                                  element.download = `${insight.title?.slice(0, 20) || 'insight'}_insight.txt`;
                                  document.body.appendChild(element);
                                  element.click();
                                  document.body.removeChild(element);
                                }}
                                size="small"
                                aria-label="Download Insight"
                                className="insight-action-btn"
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {!sharedMode && (
                              <>
                              <InsightSocialShare insight={insight} />
                              <Tooltip title="Delete Insight">
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteModal(insight.id);
                                  }}
                                  size="small"
                                  aria-label="Delete Insight"
                                  className="insight-action-btn delete-btn"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              </>
                            )}
                          </div>
                        </div>
                      ))}

                      {totalPages > 1 && (
                        <div className="pagination">
                          <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                          >
                            Previous
                          </button>
                          <span className="pagination-info">
                            Page {currentPage} of {totalPages}
                          </span>
                          <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                          >
                            Next
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="no-insights">
                      <p>No insights or reports available for this project.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Delete Insight Confirmation Dialog */}
              <Dialog open={showDeleteModal} onClose={closeDeleteModal}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete this insight?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeDeleteModal}>Cancel</Button>
                  <Button onClick={deleteInsight} color="error">Delete</Button>
                </DialogActions>
              </Dialog>

              {/* Delete Task Confirmation Dialog */}
              <Dialog open={showDeleteTaskModal} onClose={() => setShowDeleteTaskModal(false)}>
                <DialogTitle>Confirm Task Deletion</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete this task?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDeleteTaskModal(false)}>Cancel</Button>
                  <Button onClick={confirmTaskDelete} color="error">Delete</Button>
                </DialogActions>
              </Dialog>

              {/* Reset Project Confirmation Dialog */}
              <Dialog open={showResetProjectModal} onClose={() => setShowResetProjectModal(false)}>
                <DialogTitle>Confirm Project Reset</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to reset this project? This will:<br />
                    - Delete all pending tasks<br />
                    - Create new tasks based on the project's description<br />
                    - Preserve all existing insights<br />
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowResetProjectModal(false)}>Cancel</Button>
                  <Button onClick={confirmProjectReset} color="primary">Reset</Button>
                </DialogActions>
              </Dialog>

              {/* Delete Project Confirmation Dialog */}
              <Dialog open={showDeleteProjectModal} onClose={() => setShowDeleteProjectModal(false)}>
                <DialogTitle>Confirm Project Deletion</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to delete this project? This action cannot be undone.
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowDeleteProjectModal(false)}>Cancel</Button>
                  <Button onClick={confirmProjectDelete} color="error">Delete</Button>
                </DialogActions>
              </Dialog>

              {/* Pause/Resume Project Confirmation Dialog */}
              <Dialog open={showPauseProjectModal} onClose={() => setShowPauseProjectModal(false)}>
                <DialogTitle>Confirm Project {project.status === 'active' ? 'Pause' : 'Resume'}</DialogTitle>
                <DialogContent>
                  <DialogContentText>
                    Are you sure you want to {project.status === 'active' ? 'pause' : 'resume'} this project?
                  </DialogContentText>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setShowPauseProjectModal(false)}>Cancel</Button>
                  <Button onClick={confirmProjectPause} color="primary">
                    {project.status === 'active' ? 'Pause' : 'Resume'}
                  </Button>
                </DialogActions>
              </Dialog>

              {/* Copy Notification */}
              {copyNotification && (
                <div className="copy-notification">
                  <span>Copied to clipboard!</span>
                </div>
              )}

              {/* Save Message */}
              {saveMessage && (
                <div className="save-message">
                  <span>{saveMessage}</span>
                </div>
              )}

              <style>{`
                .insight-card {
                  border: 1px solid #e0e0e0;
                  border-radius: 0.5rem;
                  padding: 1rem;
                  margin-bottom: 1rem;
                  cursor: pointer;
                  transition: background-color 0.3s ease, box-shadow 0.3s ease;
                }
                .insight-card:hover {
                  background-color: #f8f9fa;
                  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
                .insight-header {
                  display: flex;
                  flex-direction: column;
                  margin-bottom: 0.5rem;
                  position: relative;
                }
                .insight-title {
                  margin-bottom: 0.5rem;
                  color: #2c3e50;
                  font-weight: 600;
                  padding-right: 3rem;
                }
                .insight-meta {
                  font-size: 0.875rem;
                  color: #6c757d;
                }
                .insight-meta span {
                  margin-left: 1rem;
                }
                .insight-expand-indicator {
                  position: absolute;
                  top: 0;
                  right: 0;
                  z-index: 2;
                }
                .expand-indicator-btn {
                  color: #007bff !important;
                  transition: all 0.2s ease !important;
                  border-radius: 50% !important;
                  padding: 0.5rem !important;
                  min-width: 40px !important;
                  height: 40px !important;
                  background-color: rgba(0, 123, 255, 0.1) !important;
                }
                .expand-indicator-btn:hover {
                  background-color: rgba(0, 123, 255, 0.2) !important;
                  color: #0056b3 !important;
                  transform: translateY(-1px);
                }
                .expand-indicator-btn:active {
                  transform: translateY(0);
                }
                .insight-summary {
                  margin-bottom: 0.5rem;
                }
                .insight-content {
                  margin-top: 0.5rem;
                  padding: 0.75rem;
                  background-color: #f8f9fa;
                  border-radius: 0.375rem;
                  border-left: 3px solid #007bff;
                }
                .insight-actions {
                  display: flex;
                  justify-content: flex-end;
                  margin-top: 0.5rem;
                  flex-wrap: wrap;
                  gap: 0.25rem;
                  align-items: center;
                }
                .insight-action-btn {
                  color: #6c757d !important;
                  transition: all 0.2s ease !important;
                  border-radius: 50% !important;
                  padding: 0.375rem !important;
                  min-width: 32px !important;
                  height: 32px !important;
                }
                .insight-action-btn:hover {
                  background-color: rgba(0, 0, 0, 0.04) !important;
                  color: #495057 !important;
                  transform: translateY(-1px);
                }
                .insight-action-btn.delete-btn {
                  color: #dc3545 !important;
                }
                .insight-action-btn.delete-btn:hover {
                  background-color: rgba(220, 53, 69, 0.1) !important;
                  color: #c82333 !important;
                }
                .insight-action-btn:active {
                  transform: translateY(0);
                }

                /* Project Header Styles */
                .project-header {
                  display: flex;
                  flex-direction: column;
                  margin-bottom: 1rem;
                  gap: 1rem;
                }

                .project-title {
                  width: 100%;
                  font-size: 2rem;
                  font-weight: 700;
                  margin: 0;
                  padding: 0.5rem 1rem;
                  white-space: nowrap;
                  overflow: hidden;
                  textOverflow: ellipsis;
                }

                @media (max-width: 768px) {
                  .project-header {
                    gap: 0.5rem;
                  }
                  .project-title {
                    font-size: 1.3rem;
                    padding: 0.5rem 0;
                    white-space: normal;
                  }
                  .insight-title {
                    padding-right: 2.5rem;
                  }
                  .expand-indicator-btn {
                    min-width: 36px !important;
                    height: 36px !important;
                    padding: 0.375rem !important;
                  }
                  .insight-actions {
                    gap: 0.125rem;
                    justify-content: center;
                  }
                  .insight-action-btn {
                    min-width: 28px !important;
                    height: 28px !important;
                    padding: 0.25rem !important;
                  }
                }

                .project-actions {
                  display: flex;
                  gap: 0.5rem;
                  flex-wrap: wrap;
                  width: 100%;
                }

                .project-actions button {
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  flex: 1;
                  min-width: 120px;
                  justify-content: center;
                  white-space: nowrap;
                }

                .card-title {
                  cursor: pointer;
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                }

                .card-title:hover .fa-edit {
                  opacity: 1;
                }

                .fa-edit {
                  opacity: 0;
                  transition: opacity 0.2s ease;
                  font-size: 0.875rem;
                }

                .card-description {
                  cursor: pointer;
                  display: flex;
                  align-items: flex-start;
                  gap: 0.5rem;
                }

                .card-description > div {
                  width: 100%;
                }

                /* Project Status Styles */
                .project-status {
                  margin: 1rem 0;
                }

                .status-badge {
                  display: inline-block;
                  padding: 0.25rem 0.75rem;
                  border-radius: 1rem;
                  font-size: 0.875rem;
                  font-weight: 500;
                  text-transform: capitalize;
                }

                .status-badge.active {
                  background-color: #28a745;
                  color: white;
                }

                .status-badge.paused {
                  background-color: #ffc107;
                  color: #000;
                }

                .status-badge.completed {
                  background-color: #17a2b8;
                  color: white;
                }

                /* Updated Pending Tasks Styles */
                .pending-tasks-section {
                  margin-top: 2rem;
                  background-color: #f8f9fa;
                  border-radius: 0.75rem;
                  padding: 1.5rem;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                  width: 100%;
                }

                .pending-tasks-header {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  cursor: pointer;
                  padding-bottom: 1rem;
                  border-bottom: 2px solid #e9ecef;
                  flex-wrap: wrap;
                  gap: 1rem;
                }

                .pending-tasks-header h5 {
                  display: flex;
                  align-items: center;
                  color: #2c3e50;
                  font-weight: 600;
                  margin: 0;
                  flex: 1;
                  min-width: 200px;
                }

                .task-count {
                  font-size: 0.875rem;
                  color: #6c757d;
                  margin-left: 0.5rem;
                  background-color: #e9ecef;
                  padding: 0.25rem 0.5rem;
                  border-radius: 1rem;
                }

                .toggle-icon {
                  margin-left: 0.5rem;
                  color: #6c757d;
                  font-size: 0.875rem;
                }

                .add-task-btn {
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  padding: 0.5rem 1rem;
                  border-radius: 0.5rem;
                  font-weight: 500;
                  transition: all 0.2s ease;
                  white-space: nowrap;
                }

                .task-list {
                  list-style: none;
                  padding: 0;
                  margin: 1rem 0 0 0;
                  width: 100%;
                }

                .task-item {
                  background-color: white;
                  border-radius: 0.75rem;
                  margin-bottom: 1rem;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
                  transition: transform 0.2s ease, box-shadow 0.2s ease;
                  width: 100%;
                }

                .task-main {
                  display: flex;
                  padding: 1.25rem;
                  gap: 1rem;
                  flex-wrap: wrap;
                }

                .task-icon-wrapper {
                  display: flex;
                  align-items: flex-start;
                  padding-top: 0.25rem;
                }

                .task-icon {
                  font-size: 1.25rem;
                  background-color: #f8f9fa;
                  padding: 0.5rem;
                  border-radius: 0.5rem;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                }

                .task-content {
                  flex: 1;
                  min-width: 250px;
                }

                .task-text {
                  font-size: 1rem;
                  color: #2c3e50;
                  margin-bottom: 0.75rem;
                  line-height: 1.5;
                }

                .task-details {
                  display: flex;
                  flex-wrap: wrap;
                  gap: 0.75rem;
                  align-items: center;
                  margin-bottom: 0.75rem;
                  width: 100%;
                }

                .task-badges {
                  display: flex;
                  gap: 0.5rem;
                  flex-wrap: wrap;
                }

                .task-badge {
                  font-size: 0.75rem;
                  padding: 0.25rem 0.75rem;
                  border-radius: 1rem;
                  font-weight: 500;
                  text-transform: capitalize;
                }

                .task-badge.immediate {
                  background-color: #e3f2fd;
                  color: #1976d2;
                }

                .task-badge.recurring {
                  background-color: #e8f5e9;
                  color: #2e7d32;
                }

                .task-badge.conclusion {
                  background-color: #fff3e0;
                  color: #f57c00;
                }

                .task-frequency {
                  font-size: 0.75rem;
                  padding: 0.25rem 0.75rem;
                  border-radius: 1rem;
                  background-color: #f3e5f5;
                  color: #7b1fa2;
                }

                .task-last-run {
                  font-size: 0.75rem;
                  color: #6c757d;
                }

                .incident-info {
                  background-color: #f8f9fa;
                  border-radius: 0.5rem;
                  padding: 1rem;
                  margin-top: 0.75rem;
                }

                .incident-section {
                  margin-bottom: 0.5rem;
                }

                .incident-section:last-child {
                  margin-bottom: 0;
                }

                .incident-label {
                  font-weight: 500;
                  color: #495057;
                  margin-right: 0.5rem;
                }

                .incident-text {
                  color: #6c757d;
                }

                .task-actions {
                  display: flex;
                  gap: 0.5rem;
                  margin-top: 1rem;
                  flex-wrap: wrap;
                  width: 100%;
                }

                .task-actions button {
                  padding: 0.375rem 0.75rem;
                  border-radius: 0.375rem;
                  transition: all 0.2s ease;
                  flex: 1;
                  min-width: 100px;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 0.5rem;
                }

                @media (max-width: 768px) {
                  .pending-tasks-section {
                    padding: 1rem;
                  }

                  .pending-tasks-header {
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.5rem;
                  }

                  .pending-tasks-header h5 {
                    width: 100%;
                  }

                  .add-task-btn {
                    width: 100%;
                    justify-content: center;
                  }

                  .task-main {
                    padding: 1rem;
                  }

                  .task-icon-wrapper {
                    display: none;
                  }

                  .task-content {
                    min-width: 100%;
                  }

                  .task-details {
                    flex-direction: column;
                    align-items: flex-start;
                  }

                  .task-actions {
                    flex-direction: column;
                  }

                  .task-actions button {
                    width: 100%;
                  }
                }

                @media (max-width: 480px) {
                  .pending-tasks-section {
                    padding: 0.75rem;
                  }

                  .task-main {
                    padding: 0.75rem;
                  }

                  .task-text {
                    font-size: 0.875rem;
                  }

                  .task-badge {
                    font-size: 0.7rem;
                  }
                  .insight-title {
                    padding-right: 2rem;
                  }
                  .expand-indicator-btn {
                    min-width: 32px !important;
                    height: 32px !important;
                    padding: 0.25rem !important;
                  }
                  .insight-actions {
                    gap: 0.125rem;
                    justify-content: center;
                    flex-wrap: wrap;
                  }
                  .insight-action-btn {
                    min-width: 24px !important;
                    height: 24px !important;
                    padding: 0.125rem !important;
                  }
                }

                /* Success Notification Styles */
                .success-notification {
                  position: fixed;
                  bottom: 30px;
                  left: 50%;
                  transform: translateX(-50%);
                  background-color: #2ecc71;
                  color: white;
                  padding: 12px 24px;
                  border-radius: 8px;
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                  z-index: 1001;
                  animation: slideUp 0.3s ease-out;
                }

                @keyframes slideUp {
                  from {
                    transform: translate(-50%, 100%);
                    opacity: 0;
                  }
                  to {
                    transform: translate(-50%, 0);
                    opacity: 1;
                  }
                }

                /* Modal Styles */
                .modal-overlay {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  bottom: 0;
                  background-color: rgba(0, 0, 0, 0.5);
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  z-index: 1000;
                }

                .modal-content {
                  background-color: white;
                  padding: 24px;
                  border-radius: 12px;
                  width: 90%;
                  max-width: 500px;
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                }

                .modal-content h2 {
                  margin-bottom: 20px;
                  color: #2c3e50;
                }

                .form-group {
                  margin-bottom: 20px;
                }

                .form-group label {
                  display: block;
                  margin-bottom: 8px;
                  color: #4a5568;
                  font-weight: 500;
                }

                .form-group input,
                .form-group select,
                .form-group textarea {
                  width: 100%;
                  padding: 10px;
                  border: 1px solid #e2e8f0;
                  border-radius: 6px;
                  font-size: 14px;
                }

                .form-group input:focus,
                .form-group select:focus,
                .form-group textarea:focus {
                  outline: none;
                  border-color: #3498db;
                  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
                }

                .modal-actions {
                  display: flex;
                  justify-content: flex-end;
                  gap: 12px;
                  margin-top: 24px;
                }

                .modal-actions button {
                  padding: 10px 20px;
                  border-radius: 6px;
                  font-weight: 500;
                  transition: all 0.2s ease;
                }

                .modal-actions button:hover {
                  transform: translateY(-1px);
                }

                .no-tasks {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  padding: 2rem;
                  text-align: center;
                  color: #6c757d;
                }

                .no-tasks p {
                  margin: 0;
                  font-size: 1rem;
                }

                .task-edit-actions {
                  display: flex;
                  gap: 1rem;
                  margin-top: 1rem;
                  flex-wrap: wrap;
                }

                .task-edit-actions button {
                  display: flex;
                  align-items: center;
                  gap: 0.5rem;
                  padding: 0.5rem 1rem;
                  min-width: 100px;
                  justify-content: center;
                }

                @media (max-width: 480px) {
                  .task-edit-actions {
                    flex-direction: column;
                    width: 100%;
                  }

                  .task-edit-actions button {
                    width: 100%;
                  }
                }

                /* Save Message Styles */
                .save-message {
                  position: fixed;
                  bottom: 30px;
                  left: 50%;
                  transform: translateX(-50%);
                  background-color: #2ecc71;
                  color: white;
                  padding: 12px 24px;
                  border-radius: 8px;
                  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
                  z-index: 1001;
                  animation: slideUp 0.3s ease-out;
                }

                @keyframes slideUp {
                  from {
                    transform: translate(-50%, 100%);
                    opacity: 0;
                  }
                  to {
                    transform: translate(-50%, 0);
                    opacity: 1;
                  }
                }

                .project-actions-shared {
                  display: flex;
                  align-items: flex-start;
                  width: 100%;
                  gap: 0.5rem;
                }
                .project-actions-shared button {
                  min-width: 120px;
                  width: auto;
                  flex: none;
                }

                .dark-mode .insight-card,
                .dark-mode .pending-tasks-section,
                .dark-mode .modal-content {
                  background-color: #222328;
                  border-color: #322f2f;
                  color: #ffffff;
                }

                .dark-mode .insight-card:hover {
                  background-color: #1a191d;
                }

                .dark-mode .insight-title,
                .dark-mode .project-title,
                .dark-mode .pending-tasks-header h5,
                .dark-mode .task-item,
                .dark-mode .incident-info,
                .dark-mode .modal-content h3 {
                  color: #ffffff;
                }

                .dark-mode .insight-meta,
                .dark-mode .task-count,
                .dark-mode .toggle-icon,
                .dark-mode .modal-content p {
                  color: #9c9fa6;
                }

                .dark-mode .insight-content {
                  background-color: #151519;
                  border-left-color: #b66dff;
                }

                .dark-mode .expand-indicator-btn {
                  color: #b66dff !important;
                  background-color: rgba(182, 109, 255, 0.16) !important;
                }

                .dark-mode .expand-indicator-btn:hover {
                  background-color: rgba(182, 109, 255, 0.26) !important;
                  color: #da8cff !important;
                }

                .dark-mode .insight-action-btn {
                  color: #9c9fa6 !important;
                }

                .dark-mode .insight-action-btn:hover {
                  background-color: rgba(255, 255, 255, 0.06) !important;
                  color: #ffffff !important;
                }

                .dark-mode .pending-tasks-header {
                  border-bottom-color: #322f2f;
                }

                .dark-mode .task-count {
                  background-color: #151519;
                }

                .dark-mode .task-item {
                  background-color: #2a2b32;
                  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
                }

                .dark-mode .task-text {
                  color: #e0e0e0;
                }

                .dark-mode .task-icon {
                  background-color: #1e1f25;
                }

                .dark-mode .task-badge.immediate {
                  background-color: rgba(25, 118, 210, 0.2);
                  color: #64b5f6;
                }

                .dark-mode .task-badge.recurring {
                  background-color: rgba(46, 125, 50, 0.2);
                  color: #81c784;
                }

                .dark-mode .task-badge.conclusion {
                  background-color: rgba(245, 124, 0, 0.2);
                  color: #ffb74d;
                }

                .dark-mode .task-frequency {
                  background-color: rgba(123, 31, 162, 0.2);
                  color: #ce93d8;
                }

                .dark-mode .task-last-run {
                  color: #9c9fa6;
                }

                .dark-mode .incident-info {
                  background-color: #1e1f25;
                }

                .dark-mode .incident-label {
                  color: #c0c0c0;
                }

                .dark-mode .incident-text {
                  color: #9c9fa6;
                }

                .dark-mode .no-tasks {
                  color: #9c9fa6;
                }

                .dark-mode .modal-content h2 {
                  color: #ffffff;
                }

                .dark-mode .form-group label {
                  color: #c0c0c0;
                }

                .dark-mode .form-group input,
                .dark-mode .form-group select,
                .dark-mode .form-group textarea {
                  background-color: #1e1f25;
                  border-color: #3a3b42;
                  color: #e0e0e0;
                }

                .dark-mode .form-group input:focus,
                .dark-mode .form-group select:focus,
                .dark-mode .form-group textarea:focus {
                  border-color: #b66dff;
                  box-shadow: 0 0 0 3px rgba(182, 109, 255, 0.15);
                }

                .dark-mode .task-edit-form .form-control {
                  background-color: #1e1f25;
                  border-color: #3a3b42;
                  color: #e0e0e0;
                }

                .dark-mode .save-message {
                  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
                }
              `}</style>
            </div>
          </div>
        </div>
      </div>

      {/* Unauthorized Notification */}
      <Snackbar
        open={unauthorizedNotificationOpen}
        autoHideDuration={null}
        onClose={handleUnauthorizedNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleUnauthorizedNotificationClose}
          severity="warning"
          sx={{ width: '100%' }}
          action={
            <Button
              color="inherit"
              size="small"
              href="/my_projects"
              sx={{ color: 'white', textDecoration: 'underline' }}
            >
              Sign In
            </Button>
          }
        >
          Please sign in to continue.
        </Alert>
      </Snackbar>
    </div>
  );
}

export default Project;
